/**
 * CoverageAuditModelInspector
 *
 * Phase B-mini Inspector — surfaces the L2 patterns aggregation built
 * on top of `coverage_issue_feedback` rows. Audience: clinical dev
 * staff who tune the engine. Mission per the learning-loop spec §4:
 * make engine quality observable, then provide a path to tune it.
 *
 * Sections:
 *   1. Header KPIs — total feedback rows, pipeline share, top
 *      rejection-rate detector
 *   2. Detectors tab — list ranked by reject rate, click to drill into
 *      per-section patterns
 *   3. Per-section drill-down — top rejection-reason clusters + sample
 *      issue ids that link back to the encounter detail page
 *
 * What this is NOT (deferred to Phase C):
 *   - L3 change proposals
 *   - Pre-merge evaluation engine
 *   - L4 prompt-delta proposals
 *
 * Backend: GET /v1/coverage-audit/patterns?windowDays=N&minN=M
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';

type AuditStage = 'recording' | 'summary_pipeline' | 'note_pipeline' | 'unknown';

interface ReasonCluster {
  representativeText: string;
  count: number;
  reasons: string[];
}

interface SectionPattern {
  targetSection: string | null;
  totalFeedback: number;
  accepted: number;
  rejected: number;
  edited: number;
  ignored: number;
  acceptRateAdjusted: number;
  ignoreRate: number;
  topReasons: ReasonCluster[];
  sampleRejectedIssueIds: string[];
}

interface DetectorBreakdown {
  detectorId: string;
  stage: AuditStage;
  totalFeedback: number;
  accepted: number;
  rejected: number;
  edited: number;
  ignored: number;
  acceptRateAdjusted: number;
  ignoreRate: number;
  perSectionPatterns: SectionPattern[];
}

interface PatternsResponse {
  scope: {
    windowDays: number;
    since: string;
    minFeedbackRows: number;
    stages: AuditStage[] | null;
  };
  totalFeedbackRows: number;
  detectors: DetectorBreakdown[];
}

const STAGE_LABEL: Record<AuditStage, string> = {
  recording: 'Recording',
  summary_pipeline: 'Summary pipeline',
  note_pipeline: 'Note pipeline',
  unknown: 'Unknown',
};

const STAGE_TONE: Record<AuditStage, string> = {
  recording: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  summary_pipeline: 'bg-purple-50 text-purple-700 border-purple-200',
  note_pipeline: 'bg-blue-50 text-blue-700 border-blue-200',
  unknown: 'bg-gray-50 text-gray-700 border-gray-200',
};

const WINDOW_OPTIONS = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 365, label: '1 year' },
];

const STAGE_FILTER_OPTIONS: Array<{
  value: 'all' | 'pipeline' | 'recording';
  label: string;
}> = [
  { value: 'all', label: 'All stages' },
  { value: 'pipeline', label: 'Pipeline only' },
  { value: 'recording', label: 'Recording only' },
];

export default function CoverageAuditModelInspector() {
  const [windowDays, setWindowDays] = useState(30);
  const [stageFilter, setStageFilter] = useState<'all' | 'pipeline' | 'recording'>('all');
  const [minN, setMinN] = useState(1);
  const [data, setData] = useState<PatternsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedDetector, setExpandedDetector] = useState<string | null>(null);

  const stagesParam = useMemo(() => {
    if (stageFilter === 'all') return '';
    if (stageFilter === 'pipeline') return 'summary_pipeline,note_pipeline';
    return 'recording';
  }, [stageFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('windowDays', String(windowDays));
      params.set('minN', String(minN));
      if (stagesParam) params.set('stages', stagesParam);
      const resp = await apiService.get<PatternsResponse>(
        `/v1/coverage-audit/patterns?${params.toString()}`,
      );
      setData(resp);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [windowDays, stagesParam, minN]);

  useEffect(() => {
    void load();
  }, [load]);

  // KPIs derived from response
  const kpis = useMemo(() => {
    if (!data) return null;
    const pipelineDetectors = data.detectors.filter(
      (d) => d.stage === 'summary_pipeline' || d.stage === 'note_pipeline',
    );
    const recordingDetectors = data.detectors.filter((d) => d.stage === 'recording');
    const pipelineFeedback = pipelineDetectors.reduce((acc, d) => acc + d.totalFeedback, 0);
    const recordingFeedback = recordingDetectors.reduce((acc, d) => acc + d.totalFeedback, 0);
    const total = pipelineFeedback + recordingFeedback;
    const pipelineShare = total === 0 ? 0 : pipelineFeedback / total;
    const topRejectDetector = [...data.detectors]
      .filter((d) => d.totalFeedback >= 3)
      .sort((a, b) => {
        const ar = a.totalFeedback === 0 ? 0 : a.rejected / a.totalFeedback;
        const br = b.totalFeedback === 0 ? 0 : b.rejected / b.totalFeedback;
        return br - ar;
      })[0];
    return { pipelineShare, total, topRejectDetector };
  }, [data]);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-4">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Coverage Audit — Model Inspector
          </h1>
          <p className="text-sm text-gray-600">
            What clinicians have accepted or rejected, grouped by detector + section.
            Use this to spot which detectors are noisy and which patterns warrant
            prompt tuning. Per learning-loop spec §4.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Link
            to="/management/coverage-audit"
            className="text-purple-700 hover:text-purple-900 underline"
          >
            ← Encounters
          </Link>
          <Link
            to="/management/coverage-audit/stages"
            className="text-purple-700 hover:text-purple-900 underline"
          >
            Stage dashboard
          </Link>
        </div>
      </header>

      {/* Filters */}
      <div className="flex items-center gap-4 text-sm bg-white border border-gray-200 rounded p-3">
        <label className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-gray-500">Window</span>
          <select
            value={windowDays}
            onChange={(e) => setWindowDays(parseInt(e.target.value, 10))}
            className="border border-gray-300 rounded px-2 py-1 bg-white"
          >
            {WINDOW_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-gray-500">Stage</span>
          <select
            value={stageFilter}
            onChange={(e) =>
              setStageFilter(e.target.value as 'all' | 'pipeline' | 'recording')
            }
            className="border border-gray-300 rounded px-2 py-1 bg-white"
          >
            {STAGE_FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-gray-500">
            Min N per tuple
          </span>
          <input
            type="number"
            min={1}
            max={50}
            value={minN}
            onChange={(e) => setMinN(parseInt(e.target.value, 10) || 1)}
            className="w-16 border border-gray-300 rounded px-2 py-1 bg-white"
          />
        </label>
      </div>

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
          {error}
        </div>
      )}

      {/* KPIs */}
      {data && kpis && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <KpiCard
            label="Total feedback rows"
            value={String(data.totalFeedbackRows)}
            sub={`${data.detectors.length} detectors with feedback`}
          />
          <KpiCard
            label="Pipeline share"
            value={`${(kpis.pipelineShare * 100).toFixed(0)}%`}
            sub="vs recording-stage feedback (engine learning fuel)"
          />
          <KpiCard
            label="Top reject-rate detector"
            value={
              kpis.topRejectDetector
                ? kpis.topRejectDetector.detectorId
                : '— none with N≥3 —'
            }
            sub={
              kpis.topRejectDetector
                ? `${(
                    (kpis.topRejectDetector.rejected /
                      Math.max(1, kpis.topRejectDetector.totalFeedback)) *
                      100
                  ).toFixed(0)}% reject · N=${kpis.topRejectDetector.totalFeedback}`
                : ''
            }
          />
        </div>
      )}

      {/* L3 — Auto-generated change proposals */}
      <ProposalsBlock />

      {/* Detector list */}
      {loading && !data ? (
        <div className="text-sm text-gray-500 italic border rounded p-4 bg-gray-50">
          Loading patterns…
        </div>
      ) : data && data.detectors.length === 0 ? (
        <div className="text-sm text-gray-500 italic border rounded p-4 bg-gray-50">
          No feedback rows yet for the selected window. Click Accept / Reject /
          Edit / Ignore on issues in the Coverage Audit detail page to start
          building the corpus.
        </div>
      ) : (
        <div className="space-y-2">
          {(data?.detectors ?? []).map((det) => (
            <DetectorRow
              key={det.detectorId}
              detector={det}
              expanded={expandedDetector === det.detectorId}
              onToggle={() =>
                setExpandedDetector(
                  expandedDetector === det.detectorId ? null : det.detectorId,
                )
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="rounded border border-gray-200 bg-white p-3">
      <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
        {label}
      </div>
      <div className="text-2xl font-bold text-gray-900 mt-1">{value}</div>
      <div className="text-[11px] text-gray-600 mt-1">{sub}</div>
    </div>
  );
}

function DetectorRow({
  detector,
  expanded,
  onToggle,
}: {
  detector: DetectorBreakdown;
  expanded: boolean;
  onToggle: () => void;
}) {
  const rejectPct = detector.totalFeedback === 0 ? 0 : detector.rejected / detector.totalFeedback;
  return (
    <div className="border border-gray-200 rounded bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 text-left"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <span
            className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded border ${STAGE_TONE[detector.stage]}`}
          >
            {STAGE_LABEL[detector.stage]}
          </span>
          <span className="font-mono text-sm text-gray-800 truncate">
            {detector.detectorId}
          </span>
        </div>
        <div className="flex items-center gap-4 text-sm shrink-0">
          <span className="text-gray-700">
            N=<span className="font-semibold">{detector.totalFeedback}</span>
          </span>
          <span className={rejectPct >= 0.5 ? 'text-red-700' : 'text-gray-700'}>
            reject{' '}
            <span className="font-semibold">{(rejectPct * 100).toFixed(0)}%</span>
          </span>
          <span className="text-gray-600 text-xs">
            a:{detector.accepted}/r:{detector.rejected}/e:{detector.edited}/i:
            {detector.ignored}
          </span>
          <span className="text-gray-400 text-xs">
            {expanded ? '▼' : '▶'}
          </span>
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          {detector.perSectionPatterns.length === 0 ? (
            <div className="text-xs text-gray-500 italic mt-2">
              All feedback at this detector is below the per-section minimum (
              {detector.totalFeedback} rows but no section ≥ minN). Adjust minN above
              to surface them.
            </div>
          ) : (
            <div className="space-y-3 mt-3">
              {detector.perSectionPatterns.map((sec) => (
                <SectionPatternRow key={sec.targetSection ?? '(none)'} pattern={sec} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SectionPatternRow({ pattern }: { pattern: SectionPattern }) {
  const sectionLabel = pattern.targetSection ?? '(no specific section)';
  const rejectPct =
    pattern.totalFeedback === 0 ? 0 : pattern.rejected / pattern.totalFeedback;
  return (
    <div className="border border-gray-100 rounded bg-gray-50 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-sm text-gray-800">
          target section: <span className="font-mono">{sectionLabel}</span>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-gray-600">N={pattern.totalFeedback}</span>
          <span className={rejectPct >= 0.5 ? 'text-red-700' : 'text-gray-700'}>
            reject {(rejectPct * 100).toFixed(0)}%
          </span>
          <span className="text-gray-500">
            a:{pattern.accepted}/r:{pattern.rejected}/e:{pattern.edited}/i:
            {pattern.ignored}
          </span>
        </div>
      </div>

      {pattern.topReasons.length > 0 && (
        <div className="text-xs space-y-1.5">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
            Top rejection reasons
          </div>
          {pattern.topReasons.map((cluster, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="font-semibold text-red-700 shrink-0">
                ×{cluster.count}
              </span>
              <span className="text-gray-700 italic">
                &ldquo;{cluster.representativeText}&rdquo;
              </span>
            </div>
          ))}
        </div>
      )}

      {pattern.sampleRejectedIssueIds.length > 0 && (
        <div className="text-[11px] text-gray-500 mt-2">
          Sample rejected issues:{' '}
          {pattern.sampleRejectedIssueIds.map((id) => (
            <span
              key={id}
              className="font-mono mr-2"
              title={id}
            >
              {id.slice(0, 8)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ----- L3 change-proposals block -----

interface ChangeProposal {
  id: string;
  kind: 'soften_rule' | 'add_rule';
  targetPrompt: 'summary' | 'note';
  targetSection: string | null;
  detectorId: string;
  family: string;
  title: string;
  rationale: string;
  proposedAddition: string;
  evidenceFeedbackIds: string[];
  supportCount: number;
  rejectionRate: number;
  acceptanceRate: number;
  status: string;
  promptVersionObserved: string | null;
  createdAt: string;
}

function ProposalsBlock() {
  const [proposals, setProposals] = useState<ChangeProposal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiService.get<{ proposals: ChangeProposal[] }>(
        '/v1/coverage-audit/proposals?status=pending',
      );
      setProposals(r.proposals ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const generate = async () => {
    setLoading(true);
    setError(null);
    try {
      await apiService.post('/v1/coverage-audit/proposals/generate', {
        windowDays: 60,
        minSupport: 2,
        minRejectRate: 0.5,
        minAcceptRate: 0.5,
        persist: true,
      });
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const review = async (id: string, action: 'approved' | 'rejected') => {
    try {
      await apiService.post(`/v1/coverage-audit/proposals/${id}/review`, {
        action,
        reviewNote: '',
      });
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <section className="rounded border border-purple-200 bg-purple-50/40 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-purple-900">
            Auto-generated change proposals (L3)
          </h2>
          <p className="text-xs text-gray-600">
            The engine groups feedback by (detector, section, prompt version) and proposes
            prompt-tuning changes when a tuple crosses thresholds. Human review required.
          </p>
        </div>
        <button
          onClick={generate}
          disabled={loading}
          className="text-xs bg-purple-700 text-white rounded px-3 py-1 hover:bg-purple-800 disabled:opacity-50"
        >
          {loading ? 'Working…' : 'Run generator'}
        </button>
      </div>

      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
          {error}
        </div>
      )}

      {proposals.length === 0 && !loading ? (
        <div className="text-xs text-gray-500 italic">
          No pending proposals. Run the generator after collecting feedback to surface tuning candidates.
        </div>
      ) : (
        <ul className="space-y-2">
          {proposals.map((p) => {
            const open = expanded === p.id;
            return (
              <li
                key={p.id}
                className="bg-white border border-purple-100 rounded text-xs"
              >
                <button
                  onClick={() => setExpanded(open ? null : p.id)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left"
                >
                  <span>
                    <span
                      className={`font-mono px-1.5 py-0.5 rounded mr-2 ${
                        p.kind === 'soften_rule'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-emerald-100 text-emerald-800'
                      }`}
                    >
                      {p.kind === 'soften_rule' ? 'soften' : 'add'}
                    </span>
                    <span className="font-semibold">{p.title}</span>
                  </span>
                  <span className="text-gray-500 shrink-0">
                    N={p.supportCount} · reject{(p.rejectionRate * 100).toFixed(0)}%
                  </span>
                </button>
                {open && (
                  <div className="px-3 pb-3 border-t border-purple-100 space-y-2">
                    <div className="text-gray-700 italic">{p.rationale}</div>
                    <pre className="bg-gray-50 border border-gray-200 rounded p-2 text-[11px] whitespace-pre-wrap font-mono text-gray-800">
                      {p.proposedAddition}
                    </pre>
                    <div className="text-[11px] text-gray-500">
                      Evidence: {p.evidenceFeedbackIds.length} feedback rows · prompt:{' '}
                      <span className="font-mono">{p.promptVersionObserved ?? 'null'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => void review(p.id, 'approved')}
                        className="bg-emerald-600 text-white rounded px-3 py-1 hover:bg-emerald-700"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => void review(p.id, 'rejected')}
                        className="bg-gray-300 text-gray-800 rounded px-3 py-1 hover:bg-gray-400"
                      >
                        Reject
                      </button>
                      <span className="text-[11px] text-gray-500 ml-2">
                        Approval is acknowledgement only — actual prompt edits still ship via PR.
                      </span>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
